import React, { useState, useEffect } from "react";
import { Form, Input, Select, Checkbox, message, Row, Col } from "antd";
import { Modal } from "../../../design-system";
import { BUTTON_PRESETS } from "../../../design-system/constants";
import nodeAPI from "../../../service/ApiServiceNode";
import { buildCreatedClientForProjectAutoFill } from "../Projects/utils/clientAutoFill";
import "../modal-shared.scss";
import "./ClientModal.scss";
import logger from "../../../utils/logger";



const { Option } = Select;

const ClientModal = ({ visible, mode, client, onClose, onSuccess }) => {
    // Support both 'visible' and 'open' props for backward compatibility
    const open = visible;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [industries, setIndustries] = useState([]);
    const [countries, setCountries] = useState([]);
    const [replaceIndustry, setReplaceIndustry] = useState(false);

    // Fetch industry classifications
    useEffect(() => {
        if (open) {
            fetchIndustries();
            if (mode === "add") {
                nodeAPI.get('/project/vendors/countries').then(response => {
                    setCountries(response.data?.data?.countries || []);
                }).catch(() => setCountries([]));
            }

            if (mode === "edit" && client) {
                form.setFieldsValue({
                    name: client.name,
                });
            } else {
                form.resetFields();
            }
        }
    }, [open, mode, client, form]);

    // Set industry IDs after industries are loaded in edit mode
    useEffect(() => {
        if (mode === "edit" && client && industries.length > 0) {
            // Map industry names to IDs for the multiselect
            const industryIds =
                client.industries
                    ?.map((industryName) => {
                        const industry = industries.find(
                            (ind) => ind.name === industryName
                        );
                        return industry?.id;
                    })
                    .filter(Boolean) || [];

            if (industryIds.length > 0) {
                form.setFieldsValue({
                    industry_classification_ids: industryIds,
                });
            }
        }
    }, [mode, client, industries, form]);

    const fetchIndustries = async () => {
        try {
            const response = await nodeAPI.get(
                "/project/clients/industry-classifications"
            );
            logger.debug("Industries response:", response.data);
            if (
                response.data &&
                response.data.data &&
                response.data.data.industries
            ) {
                setIndustries(response.data.data.industries);
                logger.debug("Industries set:", response.data.data.industries);
            } else if (response.data && response.data.industries) {
                setIndustries(response.data.industries);
                logger.debug(
                    "Industries set (direct):",
                    response.data.industries
                );
            } else {
                logger.debug("No industries found in response");
            }
        } catch (error) {
            logger.error("Error fetching industries:", error);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            let contactId;

            // Create new contact if needed
            if (mode === "add") {
                const contactData = {
                    first_name: values.first_name,
                    last_name: values.last_name,
                    title: values.title,
                    email: values.email,
                    phone: values.phone,
                    status: "active",
                };

                const contactResponse = await nodeAPI.post(
                    "/project/clients/contacts",
                    contactData
                );

                if (
                    contactResponse.data &&
                    contactResponse.data.data &&
                    contactResponse.data.data.contact
                ) {
                    contactId = contactResponse.data.data.contact.id;
                } else if (
                    contactResponse.data &&
                    contactResponse.data.contact
                ) {
                    contactId = contactResponse.data.contact.id;
                }
            }

            if (mode === "add" && !contactId) {
                throw new Error("Please create a contact before creating the client");
            }

            if (mode === "edit") {
                // Update existing client
                const updateData = {
                    name: values.name,
                    industry_classification_ids:
                        values.industry_classification_ids,
                };

                await nodeAPI.put(`/project/clients/${client.id}`, updateData);
                message.success("Client updated successfully");
            } else {
                // Create new client
                const clientData = {
                    name: values.name,
                    description: values.description,
                    industry_classification_ids:
                        values.industry_classification_ids,
                    client_contact_id: contactId,
                    registered_street_address: values.registered_street_address,
                    registered_country: values.registered_country,
                    registered_state_province: values.registered_state_province,
                    registered_city: values.registered_city,
                    registered_postal_code: values.registered_postal_code,
                };

                const response = await nodeAPI.post(
                    "/project/clients",
                    clientData
                );
                message.success("Client created successfully");

                // Preserve selected industries when the create response omits
                // relation metadata so the parent project form can autofill.
                const createdClient = buildCreatedClientForProjectAutoFill(
                    response.data,
                    values
                );
                onSuccess(createdClient);
            }

            if (mode === "edit") {
                onSuccess();
            }
            handleClose();
        } catch (error) {
            if (error.errorFields) return;
            logger.error("Error saving client:", error);
            message.error("Failed to save client");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        setReplaceIndustry(false);
        onClose();
    };

    return (
        <Modal
            visible={open}
            onClose={handleClose}
            width="medium"
            className="client-modal"
            maskClosable={false}
        >
            <Modal.Header
                title={mode === "edit" ? "Edit Client" : "Add Client"}
                subtitle={mode === "edit" && client ? client.name : "Create a new client"}
            />

            <Modal.Body>
            <Form form={form} layout="vertical" size="small">
                {mode === "add" ? (
                    <Row gutter={32} style={{ marginTop: '16px' }}>
                        <Col span={12}>
                            <div className="section-title" style={{ fontWeight: 'bold' }}>DETAIL</div>

                            <Form.Item
                                label="Client Name"
                                name="name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter client name",
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label="Category"
                                name="industry_classification_ids"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="-- Select Categories --"
                                    allowClear
                                >
                                    {industries.map((industry) => (
                                        <Option
                                            key={industry.id}
                                            value={industry.id}
                                        >
                                            {industry.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Registered address" name="registered_street_address">
                                <Input placeholder="Enter street address" maxLength={255} />
                            </Form.Item>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Country" name="registered_country">
                                        <Select showSearch allowClear optionFilterProp="label" placeholder="Choose a country"
                                            options={countries.map(country => ({ value: country.name, label: country.name }))}
                                            onChange={() => form.setFieldsValue({ registered_state_province: undefined, registered_city: undefined })} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="State or province" name="registered_state_province">
                                        <Input placeholder="Enter state or province" maxLength={255} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="City" name="registered_city">
                                        <Input placeholder="Enter city" maxLength={255} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Postal code" name="registered_postal_code">
                                        <Input placeholder="Enter postal code" maxLength={255} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>

                        <Col span={12}>
                            <div className="section-title" style={{ fontWeight: 'bold' }}>PIC CONTACT</div>

                            <>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                label="First name"
                                                name="first_name"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            "Please enter first name",
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Enter first name" maxLength={64} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Last name"
                                                name="last_name"
                                            >
                                                <Input placeholder="Enter last name" />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Form.Item
                                        label="Title/Position"
                                        name="title"
                                    >
                                        <Input placeholder="Enter title/position" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Email"
                                        name="email"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Please enter email",
                                            },
                                            {
                                                type: "email",
                                                message:
                                                    "Please enter a valid email",
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter email" />
                                    </Form.Item>

                                    <Form.Item label="Phone" name="phone">
                                        <Input placeholder="Enter phone number" />
                                    </Form.Item>

                            </>
                        </Col>
                    </Row>
                ) : (
                    // Edit mode - simpler layout
                    <div style={{ marginTop: '16px' }}>
                        <Form.Item
                            label="Client Name"
                            name="name"
                            rules={[
                                {
                                    required: true,
                                    message: "Please enter client name",
                                },
                            ]}
                        >
                            <Input placeholder="Enter client name" />
                        </Form.Item>

                        <Form.Item
                            label="Category"
                            name="industry_classification_ids"
                        >
                            <Select
                                mode="multiple"
                                placeholder="-- Select Categories --"
                                allowClear
                            >
                                {industries.map((industry) => (
                                    <Option
                                        key={industry.id}
                                        value={industry.id}
                                    >
                                        {industry.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item>
                            <Checkbox
                                checked={replaceIndustry}
                                onChange={(e) =>
                                    setReplaceIndustry(e.target.checked)
                                }
                            >
                                Replace the industry for all projects associated
                                with this client
                            </Checkbox>
                        </Form.Item>
                    </div>
                )}
            </Form>
            </Modal.Body>

            <Modal.Footer
                buttons={[
                    {
                        ...BUTTON_PRESETS.CANCEL,
                        key: 'cancel',
                        label: 'Cancel',
                        onClick: handleClose
                    },
                    {
                        ...BUTTON_PRESETS.SAVE,
                        key: 'save',
                        label: mode === "edit" ? 'Update Client' : 'Create Client',
                        onClick: handleSubmit,
                        loading: loading
                    }
                ]}
            />
        </Modal>
    );
};

export default ClientModal;
